import { useCallback, useEffect, useMemo, useState } from 'react';
import { useApolloClient } from '@apollo/client/react';
import debounce from 'lodash.debounce';
import * as R from 'ramda';

import { SEARCH_ASSETS_WITH_ELEMENTS } from '@atom/graph/asset';
import {
  SearchAssetsWithElementsConnection,
  SearchAssetWithElements,
} from '@atom/types/asset';

export interface AssetsSearchOptions {
  // search query
  query: string;
  // schema id of assets to search for
  schemaId?: string;
  // schema ids of assets to search for
  schemaIds?: string[];
  // parentCategory id of assets to search for
  parentCategoryId?: string;
  // number of milliseconds to debounce search at
  // defaults to 500
  debounceTime?: number;
  // character min before querying the API for results
  characterMin?: number;
  // results limit
  limit?: number;
  // denotes that only material assets should be returned
  includeMaterialAssets?: boolean;
  // denotes that only location based assets should be returned
  includeLocationBasedAssets?: boolean;
  // denotes whether to include inactive assets
  includeInactive?: boolean;
  // Each returned item will include an attributes map
  includeAttributesProperty?: boolean;
  // All attributes on returned items with have value isVisibleAsSubtext: true if this is true
  isVisibleAsSubtextAttributesOnly?: boolean;
  // The list of items returned can include sub items (elements)
  includeElements?: boolean;
}

export type Data = [
  SearchAssetsWithElementsConnection,
  {
    fetchMore: () => void;
    hasMore: boolean;
    loadingSearch: boolean;
    loadingPagination: boolean;
    error: boolean;
  },
];

type DebouncedSearch = (
  query: string,
  assets: SearchAssetWithElements[],
  schemaId?: string,
  schemaIds?: string[],
  parentCategoryId?: string,
  limit?: number,
  page?: number,
) => void;

export const useAssetsWithElementsSearch = ({
  query,
  schemaId,
  schemaIds,
  parentCategoryId,
  limit,
  characterMin = 1,
  debounceTime = 500,
  includeMaterialAssets,
  includeLocationBasedAssets,
  includeInactive,
  includeAttributesProperty,
  isVisibleAsSubtextAttributesOnly,
  includeElements,
}: AssetsSearchOptions): Data => {
  const client = useApolloClient();

  const [loadingSearch, setLoadingSearch] = useState<boolean>(false);
  const [loadingPagination, setLoadingPagination] = useState<boolean>(false);
  const [error, setError] = useState<boolean>(false);

  const [connection, setConnection] =
    useState<SearchAssetsWithElementsConnection>({
      totalCount: 0,
      assets: [],
    });

  const nextPage = useMemo(() => {
    if (!limit || !connection.totalCount) {
      return 1;
    }

    return Math.floor(connection.assets.length / limit) + 1;
  }, [connection, limit]);

  /* eslint-disable @typescript-eslint/no-shadow */
  const searchAssets = async (
    search: string,
    assets: SearchAssetWithElements[],
    schemaId?: string,
    schemaIds?: string[],
    parentCategoryId?: string,
    limit?: number,
    page?: number,
  ) => {
    /* eslint-enable @typescript-eslint/no-shadow */
    setError(false);

    try {
      const { data } = await client.query({
        query: SEARCH_ASSETS_WITH_ELEMENTS,
        variables: {
          input: {
            query: search,
            ...R.reject(R.isNil, {
              limit,
              page,
              schemaId,
              schemaIds,
              parentCategoryId,
              includeMaterialAssets,
              includeLocationBasedAssets,
              includeInactive,
              includeAttributesProperty,
              isVisibleAsSubtextAttributesOnly,
              includeElements,
            }),
          },
        },
      });

      setConnection({
        totalCount: data.assetsWithElementsSearch.totalCount,
        assets: [...assets, ...data.assetsWithElementsSearch.assets],
      });
    } catch (err) {
      setError(true);
    }

    setLoadingSearch(false);
    setLoadingPagination(false);
  };

  const searchAssetsDebounced = useCallback<DebouncedSearch>(
    debounce(searchAssets, debounceTime),
    [],
  );

  useEffect(() => {
    if (query.length >= characterMin) {
      setLoadingSearch(true);
      searchAssetsDebounced(
        query,
        [],
        schemaId,
        schemaIds,
        parentCategoryId,
        limit,
      );
    } else {
      setConnection({ totalCount: 0, assets: [] });
    }
  }, [query]);

  const hasMore = connection.assets.length < connection.totalCount;

  const fetchMore = () => {
    if (hasMore) {
      setLoadingPagination(true);
      searchAssets(
        query,
        connection.assets,
        schemaId,
        schemaIds,
        parentCategoryId,
        limit,
        nextPage,
      );
    }
  };

  return [
    connection,
    {
      fetchMore,
      hasMore,
      loadingSearch,
      loadingPagination,
      error,
    },
  ];
};
